/**
 * Shared background scroll lock for modals.
 *
 * State is stored on document.documentElement so separate block view-script
 * bundles (each with its own module copy) stay in sync.
 */

export const SCROLL_LOCK_BODY_CLASS = 'alteia-scroll-locked';
export const SCROLL_UNLOCK_EVENT = 'alteia:background-scroll-unlock';

const SCROLL_Y_ATTR = 'data-alteia-scroll-y';
const LOCK_COUNT_ATTR = 'data-alteia-scroll-lock-count';

/**
 * @return {number}
 */
function getLockCount() {
	const value = document.documentElement.getAttribute( LOCK_COUNT_ATTR );

	if ( ! value ) {
		return 0;
	}

	const parsed = parseInt( value, 10 );

	return Number.isNaN( parsed ) ? 0 : parsed;
}

/**
 * @param {number} count Active lock count.
 */
function setLockCount( count ) {
	if ( count > 0 ) {
		document.documentElement.setAttribute(
			LOCK_COUNT_ATTR,
			String( count )
		);
		return;
	}

	document.documentElement.removeAttribute( LOCK_COUNT_ATTR );
}

/**
 * @return {number|null}
 */
function getLockedScrollY() {
	const value = document.documentElement.getAttribute( SCROLL_Y_ATTR );

	if ( value === null || value === '' ) {
		return null;
	}

	const parsed = parseInt( value, 10 );

	return Number.isNaN( parsed ) ? null : parsed;
}

/**
 * @return {number}
 */
function getScrollbarWidth() {
	return Math.max(
		0,
		window.innerWidth - document.documentElement.clientWidth
	);
}

/**
 * Reserve layout space when the page scrollbar is removed.
 *
 * @param {number} width Scrollbar width in pixels.
 */
function applyScrollbarCompensation( width ) {
	if ( width <= 0 ) {
		return;
	}

	document.documentElement.style.setProperty(
		'--alteia-scrollbar-width',
		`${ width }px`
	);
	document.body.style.paddingRight = `${ width }px`;

	document
		.querySelectorAll( '.alteia-header, .wp-block-alteia-header' )
		.forEach( ( element ) => {
			element.style.paddingRight = `${ width }px`;
		} );
}

/**
 * Remove scrollbar compensation styles.
 */
function clearScrollbarCompensation() {
	document.documentElement.style.removeProperty( '--alteia-scrollbar-width' );
	document.body.style.paddingRight = '';

	document
		.querySelectorAll( '.alteia-header, .wp-block-alteia-header' )
		.forEach( ( element ) => {
			element.style.paddingRight = '';
		} );
}

/**
 * Lock background scroll while a modal is open.
 *
 * @param {Object}  [options]
 * @param {string}  [options.bodyClass]           Optional body class for modal-specific styles.
 * @param {boolean} [options.compensateScrollbar] Whether to offset layout for scrollbar width.
 */
export function lockBackgroundScroll( options = {} ) {
	const { bodyClass = '', compensateScrollbar = false } = options;
	const currentCount = getLockCount();

	if ( currentCount === 0 ) {
		const scrollY = window.scrollY || window.pageYOffset || 0;

		document.documentElement.setAttribute(
			SCROLL_Y_ATTR,
			String( scrollY )
		);

		if ( compensateScrollbar ) {
			applyScrollbarCompensation( getScrollbarWidth() );
		}

		document.body.classList.add( SCROLL_LOCK_BODY_CLASS );

		if ( bodyClass ) {
			document.body.classList.add( bodyClass );
		}

		document.body.style.position = 'fixed';
		document.body.style.top = `-${ scrollY }px`;
		document.body.style.left = '0';
		document.body.style.right = '0';
		document.body.style.width = '100%';
		document.body.style.overflow = 'hidden';
	}

	setLockCount( currentCount + 1 );
}

/**
 * Unlock background scroll and restore the saved page position.
 *
 * @param {Object} [options]
 * @param {string} [options.bodyClass] Optional body class passed to lockBackgroundScroll.
 */
export function unlockBackgroundScroll( options = {} ) {
	const { bodyClass = '' } = options;
	const currentCount = getLockCount();

	if ( currentCount <= 0 ) {
		return;
	}

	const nextCount = currentCount - 1;

	setLockCount( nextCount );

	if ( nextCount > 0 ) {
		return;
	}

	const scrollY = getLockedScrollY() || 0;

	document.body.classList.remove( SCROLL_LOCK_BODY_CLASS );

	if ( bodyClass ) {
		document.body.classList.remove( bodyClass );
	}

	document.body.style.position = '';
	document.body.style.top = '';
	document.body.style.left = '';
	document.body.style.right = '';
	document.body.style.width = '';
	document.body.style.overflow = '';
	clearScrollbarCompensation();
	document.documentElement.removeAttribute( SCROLL_Y_ATTR );

	window.scrollTo( 0, scrollY );

	document.dispatchEvent(
		new CustomEvent( SCROLL_UNLOCK_EVENT, {
			detail: { scrollY },
		} )
	);
}
